import { z } from "zod";

/**
 * The Assistant action block: one domain-free row grammar for every governed action review,
 * receipt, popover card, and phone list. An action projects its stored presentation into rows on
 * the server; the renderer never learns a domain noun. Values arrive already formatted so the block
 * only lays them out. Money is never recomputed client-side.
 */

export const ActionBlockRowStateSchema = z.enum(["ready", "needs_input", "unsupported", "no_change", "saved", "not_saved", "left_out"]);
export type ActionBlockRowState = z.infer<typeof ActionBlockRowStateSchema>;

/** Closed set of value kinds. An action picks one; it never draws its own value. */
export const ActionBlockFieldKindSchema = z.enum(["money", "quantity", "date", "text", "enum", "person"]);
export type ActionBlockFieldKind = z.infer<typeof ActionBlockFieldKindSchema>;

export const ActionBlockDeltaSchema = z.object({ label: z.string(), tone: z.enum(["neutral", "elevated"]) }).strict();
export type ActionBlockDelta = z.infer<typeof ActionBlockDeltaSchema>;

export const ActionBlockFieldSchema = z
  .object({
    key: z.string(),
    /** Quantity break, field name, or line label. */
    label: z.string(),
    kind: ActionBlockFieldKindSchema,
    /** Absent for a create; null when the value was not recorded in this review. */
    before: z.string().nullable().optional(),
    after: z.string(),
    delta: ActionBlockDeltaSchema.optional(),
    /** Long text renders as a block under its label rather than inline. */
    block: z.boolean().optional(),
  })
  .strict();
export type ActionBlockField = z.infer<typeof ActionBlockFieldSchema>;

export const ActionBlockNoteSchema = z.object({ text: z.string(), tone: z.enum(["muted", "warn", "info"]) }).strict();
export type ActionBlockNote = z.infer<typeof ActionBlockNoteSchema>;

export const ActionBlockRowSchema = z
  .object({
    key: z.string(),
    state: ActionBlockRowStateSchema,
    /** Quiet tag for anything that is not a plain update ("New cost", "Correction"). */
    kindLabel: z.string().nullable().optional(),
    subject: z.string(),
    secondary: z.string().nullable().optional(),
    fields: z.array(ActionBlockFieldSchema),
    /** One muted line naming what stays the same. */
    unchanged: z.string().nullable().optional(),
    /** Right-hand context: the new window or date, with the old one under it. */
    context: z.object({ primary: z.string(), secondary: z.string().nullable().optional() }).strict().nullable().optional(),
    notes: z.array(ActionBlockNoteSchema).optional(),
    /** Why the row needs input or cannot be saved; shown in the row body. */
    reason: z.string().nullable().optional(),
    /** Key of the review group the row renders in, when the review is grouped. */
    group: z.string().optional(),
  })
  .strict();
export type ActionBlockRow = z.infer<typeof ActionBlockRowSchema>;

export type ActionBlockMode = "review" | "receipt";

/** A run of text inside a question's summary line; strong runs carry the figures. */
export const ActionTextRunSchema = z.object({ text: z.string(), strong: z.boolean().optional() }).strict();
export type ActionTextRun = z.infer<typeof ActionTextRunSchema>;

export const ActionQuestionChoiceSchema = z
  .object({
    id: z.string().min(1).max(200),
    label: z.string().min(1),
    /** Quiet text after the label (a place, the current rate). */
    detail: z.string().min(1).optional(),
    /** The person's message when this choice is answered by reply. */
    reply: z.string().min(1).optional(),
    /** A way past the question rather than an answer to it ("Leave these out"); drawn as a quiet link. */
    quiet: z.boolean().optional(),
  })
  .strict();
export type ActionQuestionChoice = z.infer<typeof ActionQuestionChoiceSchema>;

/**
 * A closed question a row asks before it can save. `pick` answers send `{ itemKey, choiceId }` and
 * the action maps the choice; `reply` answers send the choice's words as the person's message.
 * One-choice questions render as buttons; many-choice questions as checkboxes with a submit.
 */
export const ActionRowQuestionSchema = z
  .object({
    /** Accessible name of the question's control group. */
    label: z.string().min(1),
    prompt: z.string().min(1).nullable(),
    summary: z.array(ActionTextRunSchema).nullable(),
    choices: z.array(ActionQuestionChoiceSchema).min(1),
    select: z.enum(["one", "many"]),
    answer: z.enum(["pick", "reply"]),
    /** Submit control label for a many-choice question, by how many are chosen. */
    submit: z
      .object({ one: z.string().min(1), many: z.string().min(1) })
      .strict()
      .nullable(),
    /** A many-choice reply joins the chosen replies with "and" between these words. */
    replyWrap: z.object({ before: z.string(), after: z.string() }).strict().nullable(),
    /** One more answer beside the choices, sent as the person's message. */
    alternative: z
      .object({ label: z.string().min(1), reply: z.string().min(1) })
      .strict()
      .nullable(),
  })
  .strict();
export type ActionRowQuestion = z.infer<typeof ActionRowQuestionSchema>;

/** An org-relative link; the client prefixes its org slug. */
export const ActionLinkSchema = z
  .object({
    label: z.string().min(1),
    href: z.string().regex(/^\/[^/]/),
    title: z.string().min(1).optional(),
  })
  .strict();
export type ActionLink = z.infer<typeof ActionLinkSchema>;

/** One group of a grouped review. A collapsed group holds lines, never rows. */
export const ActionBlockGroupSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    order: z.number().int().nonnegative(),
    /** Shown after the label; null shows the label alone. */
    count: z.number().int().nonnegative().nullable(),
    collapsed: z.boolean(),
  })
  .strict();
export type ActionBlockGroup = z.infer<typeof ActionBlockGroupSchema>;

/** Content synthesized from the review's scope (not items): a line or a question, outside counts and paging. */
export const ActionBlockExtraRowSchema = z.union([
  z.object({ key: z.string().min(1), group: z.string().min(1), text: z.string().min(1) }).strict(),
  z.object({ key: z.string().min(1), group: z.string().min(1), question: ActionRowQuestionSchema }).strict(),
]);
export type ActionBlockExtraRow = z.infer<typeof ActionBlockExtraRowSchema>;
